// Cutoff decisioning engine for the PROXY (ICT/Collections) Follow-Up Queue.
// Pure, dependency-free — no React, no dates-from-the-real-clock. Every input
// is a plain value the caller already has on a ProxyJob record.

import type { ProxyBand, ProxyMaterialStatus, RicsStatus } from '../data/mockProxyJobs';

export type ProxyAction =
  | 'FOLLOW_UP' | 'PULL_FILING' | 'POD' | 'EMAIL_ONLY_CANCEL_FULLSETS'
  | 'ABORT' | 'HOLD_MAIL_DATE' | 'HOLD_DISCOVERY' | 'NO_ACTION';

export interface DecisionInput {
  daysToVoteCutoff: number;
  /** Band input — see daysToMeeting(). */
  daysToMeeting: number;
  /** A filing_pulled record exists — emails released, full sets still outstanding. */
  filingPulled: boolean;
  /** Demo stand-in for the EDGAR/SEDAR lookup result. */
  filingOnPortal: boolean;
  materialStatus: ProxyMaterialStatus;
  pageCount: number;
  fullSetHolders: number;
  country: string;
  isBroadridge: boolean;
  mailDate: string | null;
  econsentOnly: boolean;
  solicitor: string | null;
  /** Fixed "as of" date to compare mailDate against — pass PROXY_SEED_TODAY. */
  today: string;
  /** Why Discovery has the job (dates changing, abort requested), if it does. */
  heldForDiscovery?: string | null;
}

export interface DecisionResult {
  action: ProxyAction;
  reason: string;
  outsideGuidelines: boolean;
  /** ICT-8: at 10-8 days, consult the manager before cancelling full sets. */
  consultManager: boolean;
}

export const POD_MAX_PAGES = 100;
export const POD_MAX_FULL_SET_HOLDERS = 50;

// Report bands count days to the MEETING (ICT-1 Yellow 19-30, ICT-8 Red 1-18,
// ICT-7 Green >30). The cutoff actions inside decide() count days to the VOTE
// CUTOFF (ICT-8).
export function bandFor(daysToMeeting: number): ProxyBand {
  if (daysToMeeting <= 18) return 'Red';
  if (daysToMeeting <= 30) return 'Yellow';
  return 'Green';
}

// daysToVoteCutoff is the literal source of truth; the meeting is that plus
// the job's cutoff-to-meeting gap.
export function daysToMeeting(job: { daysToVoteCutoff: number; meetingDate: string; voteCutoffDate: string }): number {
  const gap = Math.round((Date.parse(job.meetingDate) - Date.parse(job.voteCutoffDate)) / 86_400_000);
  return job.daysToVoteCutoff + gap;
}

// Shared display copy for an action — used by both the item-panel's chip
// (components/proxy/ProxyChips.tsx) and MATERIAL_FOLLOWUP's calc-row text
// (data/workflows/materialFollowupWorkflow.ts), so the wording is named once.
export const ACTION_LABEL: Record<ProxyAction, string> = {
  FOLLOW_UP: 'Follow Up',
  PULL_FILING: 'Pull Filing',
  POD: 'POD',
  EMAIL_ONLY_CANCEL_FULLSETS: 'Email Only, Cancel Full Sets',
  ABORT: 'Abort',
  HOLD_MAIL_DATE: 'Hold for Mail Date',
  HOLD_DISCOVERY: 'Hold for Discovery',
  NO_ACTION: 'No Action',
};

export function decide(input: DecisionInput): DecisionResult {
  const { daysToVoteCutoff, materialStatus, pageCount, fullSetHolders, country, mailDate, econsentOnly, today } = input;
  const result = (action: ProxyAction, reason: string, outsideGuidelines = false, consultManager = false): DecisionResult =>
    ({ action, reason, outsideGuidelines, consultManager });

  if (materialStatus === 'Received') {
    return result('NO_ACTION', 'Material already received.');
  }

  if (econsentOnly) {
    return result('NO_ACTION', 'All holders are e-consent, so no physical follow-up is required.');
  }

  // A date change or abort request has gone to Discovery — chasing now would
  // ask for material against dates that are about to change.
  if (input.heldForDiscovery) {
    return result('HOLD_DISCOVERY', `${input.heldForDiscovery}. Discovery is updating the job, so hold the chase until they're done.`);
  }

  if (country === 'CA' && mailDate && mailDate > today) {
    return result('HOLD_MAIL_DATE', `Intermediary mail date ${mailDate} hasn't arrived yet, so don't follow up.`);
  }

  if (daysToVoteCutoff < 5) {
    return result('ABORT', `${daysToVoteCutoff}d to vote cutoff, which is inside the 5-day abort threshold.`);
  }

  if (daysToVoteCutoff < 10) {
    return daysToVoteCutoff >= 8
      ? result('EMAIL_ONLY_CANCEL_FULLSETS', `${daysToVoteCutoff}d to vote cutoff, so send email only and cancel full sets. Consult the ICT manager first in case full sets should still be sent.`, false, true)
      : result('EMAIL_ONLY_CANCEL_FULLSETS', `${daysToVoteCutoff}d to vote cutoff, so send email only and cancel full sets.`);
  }

  if (daysToVoteCutoff === 10) {
    const eligible = pageCount <= POD_MAX_PAGES && fullSetHolders <= POD_MAX_FULL_SET_HOLDERS;
    return result(
      'POD',
      eligible
        ? `POD eligible at ${pageCount}pp and ${fullSetHolders} holders.`
        : `POD requested, but ${pageCount}pp/${fullSetHolders} holders exceeds the ${POD_MAX_PAGES}pp/${POD_MAX_FULL_SET_HOLDERS}-holder guideline.`,
      !eligible,
    );
  }

  const band = bandFor(input.daysToMeeting);

  // Red, 18-11 days to vote cutoff: search EDGAR/SEDAR before chasing anyone.
  if (band === 'Red') {
    if (input.filingPulled) {
      return result('NO_ACTION', `${daysToVoteCutoff}d to vote cutoff. Filing pulled and emails released. Full sets can still arrive; POD decision at 10 days.`);
    }
    return input.filingOnPortal
      ? result('PULL_FILING', `${daysToVoteCutoff}d to vote cutoff. Search EDGAR/SEDAR first, and if the filing is there, release emails only.`)
      : result('FOLLOW_UP', `${daysToVoteCutoff}d to vote cutoff, no filing on EDGAR/SEDAR, so send a Red follow-up.`);
  }

  if (band === 'Yellow') {
    return result('FOLLOW_UP', input.solicitor === null
      ? `${input.daysToMeeting}d to meeting, no solicitor of record, so send the Issuer follow-up.`
      : `${input.daysToMeeting}d to meeting, so follow up with the solicitor.`);
  }

  return result('NO_ACTION', `${input.daysToMeeting}d to meeting and comfortably on track.`);
}

// ICT-1 Broadridge follow-ups: Ace checks the RICS portal instead of emailing.
// Only a closed project (ask Broadridge why) or an invoiced one (ask the
// fulfillment center to find the material) needs an email.
export const RICS_STATUSES: RicsStatus[] = ['Awaiting Material', 'Material Mailed', 'Project Closed', 'Project Invoiced'];

export interface RicsAction {
  email: 'broadridge' | 'fulfillment' | null;
  micNote: string;
}

export function ricsAction(status: RicsStatus): RicsAction {
  switch (status) {
    case 'Material Mailed': return { email: null, micNote: 'Per Broadridge, material mailed.' };
    case 'Project Closed': return { email: 'broadridge', micNote: 'Per Broadridge, project closed. Asked Broadridge why the job shows closed.' };
    case 'Project Invoiced': return { email: 'fulfillment', micNote: 'Per Broadridge, the job is in Project Invoiced status. Asked the fulfillment center to locate the material.' };
    default: return { email: null, micNote: 'Per Broadridge, awaiting material.' };
  }
}
